import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui';

/**
 * Issue #861 — an in-app signature pad. The treasurer draws their signature while authorizing a
 * filing send; the drawn image (a PNG data URL) is stamped on the cover's signature line, so signing
 * the report and authorizing the send are one deliberate action. Captured per-send — never a stored
 * reusable stamp — so the parent holds the value transiently and a fresh authorization draws afresh.
 *
 * Hand-rolled on a `<canvas>` (no signature-pad dependency): pointer events draw the strokes, and each
 * stroke end emits `onChange(dataUrl)`; Clear emits `onChange(null)`.
 */
export interface SignaturePadProps {
  /** Fired with the PNG data URL after each stroke, or null when cleared. */
  onChange: (dataUrl: string | null) => void;
  disabled?: boolean;
  width?: number;
  height?: number;
  'data-testid'?: string;
}

export function SignaturePad({
  onChange,
  disabled = false,
  width = 320,
  height = 120,
  'data-testid': testId = 'signature-pad',
}: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const [hasInk, setHasInk] = useState(false);

  const context = useCallback((): CanvasRenderingContext2D | null => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d') ?? null;
    if (ctx && canvas) {
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      // Ink follows the theme (the canvas carries `text-foreground`), so the signature stays legible
      // in dark mode instead of a hardcoded near-black on a dark background.
      ctx.strokeStyle = getComputedStyle(canvas).color || '#111827';
    }
    return ctx;
  }, []);

  const pointFrom = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    // Map the client point into the canvas's own pixel space (CSS size may differ from the backing size).
    const scaleX = (canvasRef.current!.width || rect.width) / (rect.width || 1);
    const scaleY = (canvasRef.current!.height || rect.height) / (rect.height || 1);
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const handleDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    drawingRef.current = true;
    lastPointRef.current = pointFrom(e);
    // A tap with no drag still leaves a dot, so the pad is never "signed" with an empty stroke.
    const ctx = context();
    if (ctx) {
      const { x, y } = lastPointRef.current;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + 0.01, y + 0.01);
      ctx.stroke();
    }
  };

  const handleMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled || !drawingRef.current) return;
    const ctx = context();
    const from = lastPointRef.current;
    if (!ctx || !from) return;
    const to = pointFrom(e);
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
    lastPointRef.current = to;
  };

  const handleUp = () => {
    if (disabled || !drawingRef.current) return;
    drawingRef.current = false;
    lastPointRef.current = null;
    const canvas = canvasRef.current;
    if (!canvas) return;
    setHasInk(true);
    onChange(canvas.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    drawingRef.current = false;
    lastPointRef.current = null;
    setHasInk(false);
    onChange(null);
  };

  // Start each mount with a clean, transparent surface (a fresh authorization draws afresh).
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, []);

  return (
    <div className="space-y-1" data-testid={testId}>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        data-testid={`${testId}-canvas`}
        onPointerDown={handleDown}
        onPointerMove={handleMove}
        onPointerUp={handleUp}
        onPointerLeave={handleUp}
        className={`w-full touch-none rounded-md border bg-background text-foreground ${
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-crosshair border-input'
        }`}
        style={{ height }}
        aria-label="Signature pad — draw your signature"
      />
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{hasInk ? 'Signed' : 'Draw your signature above'}</span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={clear}
          disabled={disabled || !hasInk}
          data-testid={`${testId}-clear`}
        >
          Clear
        </Button>
      </div>
    </div>
  );
}
